import { useState, useEffect, useCallback } from 'react';

interface NetworkStatus {
    /** Whether the browser has network connectivity */
    isOnline: boolean;
    /** Whether we were recently offline (for recovery handling) */
    wasOffline: boolean;
    /** Timestamp of last offline event */
    lastOfflineAt: Date | null;
    /** Timestamp of when we came back online */
    lastOnlineAt: Date | null;
}

/**
 * Hook for detecting network connectivity status
 *
 * Uses browser's online/offline events to track connectivity.
 *
 * @example
 * const { isOnline, wasOffline } = useNetworkStatus();
 *
 * if (!isOnline) {
 *   return <OfflineMessage />;
 * }
 */
export function useNetworkStatus(): NetworkStatus {
    const [status, setStatus] = useState<NetworkStatus>({
        // SSR/prerender always reports `isOnline: true` so the offline banner
        // never appears in the prerendered HTML. On the client, the useEffect
        // below attaches real online/offline listeners. (Node 22 defines a
        // `navigator` global with `onLine === undefined`, which would otherwise
        // render the banner during prerender and break hydration.)
        isOnline: typeof window !== 'undefined' ? (window.navigator?.onLine ?? true) : true,
        wasOffline: false,
        lastOfflineAt: null,
        lastOnlineAt: null,
    });

    const handleOnline = useCallback(() => {
        setStatus(prev => ({
            ...prev,
            isOnline: true,
            wasOffline: prev.lastOfflineAt !== null,
            lastOnlineAt: new Date(),
        }));
    }, []);

    const handleOffline = useCallback(() => {
        setStatus(prev => ({
            ...prev,
            isOnline: false,
            wasOffline: false,
            lastOfflineAt: new Date(),
        }));
    }, []);

    useEffect(() => {
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [handleOnline, handleOffline]);

    return status;
}

export type { NetworkStatus };
